import React, { useMemo, useState } from "react";
import { X } from "lucide-react";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

/**
 * ModalComplementos — modal de seleção de complementos no cardápio público.
 *
 * Props:
 *  - produto: o documento de dlv_produtos (com grupos_complementos)
 *  - onClose(): fecha sem adicionar
 *  - onAdd({produto, quantidade, complementos_escolhidos, observacao_item}): persistir
 */
export default function ModalComplementos({ produto, onClose, onAdd }) {
  const grupos = produto.grupos_complementos || [];
  const [escolhas, setEscolhas] = useState({}); // {grupoId: [opcaoId,...]}
  const [obs, setObs] = useState("");
  const [qtd, setQtd] = useState(1);

  const toggle = (grupo, opcaoId) => {
    setEscolhas((prev) => {
      const atual = prev[grupo.id] || [];
      const maximo = grupo.maximo || 1;
      const obrig = !!grupo.obrigatorio;
      // Radio behavior quando max=1 (obrigatório ou opcional)
      if (maximo === 1) {
        // Se já está selecionado e é opcional, desmarca; caso contrário, troca
        if (atual.includes(opcaoId)) {
          return obrig ? prev : { ...prev, [grupo.id]: [] };
        }
        return { ...prev, [grupo.id]: [opcaoId] };
      }
      // Multi-select com limite
      if (atual.includes(opcaoId)) {
        return { ...prev, [grupo.id]: atual.filter((x) => x !== opcaoId) };
      }
      if (atual.length >= maximo) return prev; // ignora estouro
      return { ...prev, [grupo.id]: [...atual, opcaoId] };
    });
  };

  const podeAdicionar = useMemo(() => {
    for (const g of grupos) {
      const cnt = (escolhas[g.id] || []).length;
      const min = g.obrigatorio ? g.minimo || 1 : 0;
      if (cnt < min) return false;
    }
    return true;
  }, [grupos, escolhas]);

  const precoExtra = useMemo(() => {
    let sum = 0;
    for (const g of grupos) {
      for (const opcaoId of (escolhas[g.id] || [])) {
        const opt = (g.opcoes || []).find((o) => o.id === opcaoId);
        if (opt) sum += parseFloat(opt.preco_adicional || 0);
      }
    }
    return sum;
  }, [grupos, escolhas]);

  const subtotal = (parseFloat(produto.preco || 0) + precoExtra) * qtd;

  const handleAdd = () => {
    const complementos_escolhidos = [];
    for (const g of grupos) {
      for (const opcaoId of (escolhas[g.id] || [])) {
        const opt = (g.opcoes || []).find((o) => o.id === opcaoId);
        if (opt) {
          complementos_escolhidos.push({
            grupo_id: g.id, opcao_id: opcaoId,
            // labels só p/ display local; backend recalcula nome/preco
            _grupo_nome: g.nome, _opcao_nome: opt.nome,
            _preco_adicional: parseFloat(opt.preco_adicional || 0),
          });
        }
      }
    }
    onAdd({
      produto, quantidade: qtd,
      complementos_escolhidos,
      observacao_item: (obs || "").slice(0, 200),
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="modal-complementos"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div className="min-w-0">
            <h3 className="font-semibold truncate">{produto.nome}</h3>
            <p className="text-xs text-[var(--text-secondary)]">
              {formatBRL(produto.preco)}
              {produto.unidade_medida && produto.unidade_medida !== "unidade" && (
                <span> / {produto.unidade_medida}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-secondary)] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-4 space-y-4">
          {grupos.map((g) => {
            const cnt = (escolhas[g.id] || []).length;
            const min = g.obrigatorio ? g.minimo || 1 : 0;
            const max = g.maximo || 1;
            const subtitle = g.obrigatorio
              ? max === 1
                ? "(obrigatório — escolha 1)"
                : `(obrigatório — entre ${min} e ${max})`
              : max === 1
              ? "(opcional — 1)"
              : `(opcional — até ${max})`;
            return (
              <fieldset key={g.id} data-testid={`grupo-${g.id}`} className="border-t pt-3 border-[var(--border)] first:border-t-0 first:pt-0">
                <legend className="text-sm font-semibold">
                  {g.nome}{" "}
                  <span className="text-xs text-[var(--text-secondary)] font-normal">{subtitle}</span>
                </legend>
                <div className="space-y-1.5 mt-2">
                  {(g.opcoes || []).map((o) => {
                    const checked = (escolhas[g.id] || []).includes(o.id);
                    const disabled = o.disponivel === false
                      || (!checked && cnt >= max);
                    return (
                      <label
                        key={o.id}
                        className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${
                          checked
                            ? "border-orange-500 bg-orange-50"
                            : "border-[var(--border)]"
                        } ${disabled && !checked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-[var(--bg-secondary)]"}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <input
                            type={max === 1 ? "radio" : "checkbox"}
                            name={`grupo-${g.id}`}
                            checked={checked}
                            disabled={disabled && !checked}
                            onChange={() => toggle(g, o.id)}
                            data-testid={`opt-${o.id}`}
                          />
                          <span className="text-sm">{o.nome}</span>
                          {o.disponivel === false && (
                            <span className="text-[10px] text-red-600">indisponível</span>
                          )}
                        </div>
                        <span className="text-xs text-[var(--text-secondary)]">
                          {parseFloat(o.preco_adicional || 0) > 0
                            ? `+ ${formatBRL(o.preco_adicional)}`
                            : "+ R$ 0,00"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}

          <div>
            <label className="block text-xs font-medium mb-1">
              Observação para este item
            </label>
            <textarea
              rows={2}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              placeholder='Ex: "sem açúcar"'
              maxLength={200}
              className="w-full px-2 py-1.5 border border-[var(--border)] rounded text-xs"
              data-testid="obs-item"
            />
          </div>
        </div>

        <div className="border-t border-[var(--border)] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setQtd(Math.max(1, qtd - 1))}
                className="w-8 h-8 border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
                data-testid="qtd-minus"
              >−</button>
              <span className="font-semibold w-6 text-center" data-testid="qtd-val">{qtd}</span>
              <button
                onClick={() => setQtd(Math.min(99, qtd + 1))}
                className="w-8 h-8 border border-[var(--border)] rounded-lg hover:bg-[var(--bg-secondary)]"
                data-testid="qtd-plus"
              >+</button>
            </div>
            <span className="text-sm font-semibold">{formatBRL(subtotal)}</span>
          </div>
          <button
            onClick={handleAdd}
            disabled={!podeAdicionar}
            className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg disabled:opacity-50 font-semibold"
            data-testid="add-com-complementos"
          >
            Adicionar ao pedido
          </button>
        </div>
      </div>
    </div>
  );
}
